// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Shared helpers for reading Keycloak roles out of a JWT access token.
// Extracted from useServiceHealth so role checks (e.g. developer-only UI) can be reused.

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  if (typeof atob === 'function') {
    return atob(padded);
  }
  const buffer = (globalThis as { Buffer?: { from: (value: string, encoding: string) => { toString: (encoding: string) => string } } }).Buffer;
  if (buffer?.from) {
    return buffer.from(padded, 'base64').toString('utf-8');
  }
  return null;
};

export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const decoded = decodeBase64Url(parts[1]);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const extractKeycloakRoles = (payload: Record<string, unknown>): Set<string> => {
  const roles = new Set<string>();
  const realmAccess = payload.realm_access as { roles?: unknown } | undefined;
  if (realmAccess?.roles && Array.isArray(realmAccess.roles)) {
    realmAccess.roles.forEach((role) => {
      if (typeof role === 'string') roles.add(role);
    });
  }
  const resourceAccess = payload.resource_access as Record<string, { roles?: unknown }> | undefined;
  if (resourceAccess) {
    Object.values(resourceAccess).forEach((entry) => {
      if (!entry?.roles || !Array.isArray(entry.roles)) return;
      entry.roles.forEach((role) => {
        if (typeof role === 'string') roles.add(role);
      });
    });
  }
  return roles;
};

const normalizeRole = (value: string): string => value.trim().toLowerCase();

const matchesRequiredRole = (actualRole: string, requiredRole: string): boolean => {
  const actual = normalizeRole(actualRole);
  const required = normalizeRole(requiredRole);
  return actual === required || actual.endsWith(`:${required}`) || actual.endsWith(`.${required}`) || actual.endsWith(`_${required}`) || actual.endsWith(`-${required}`);
};

export const hasKeycloakRole = (token: string | null | undefined, role: string): boolean => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const roles = extractKeycloakRoles(payload);
  for (const actualRole of roles) {
    if (matchesRequiredRole(actualRole, role)) {
      return true;
    }
  }
  return false;
};

/** True when the access token carries the Keycloak `developer` role. */
export const isDeveloperToken = (token: string | null | undefined): boolean => hasKeycloakRole(token, 'developer');
